// ════════════════════════════════════════════════════════════════════════════
// M2.7 Madurez de Procesos — sección Análisis IA PEMM-nativo (Hammer · 0738/0740)
// Bajo el layout 2 columnas (full width). Genera/regenera el análisis V·IA del
// proceso (diagnóstico de eslabón débil + recomendaciones por categoría + DAFO +
// fortalezas/riesgos), human-in-the-loop (GENERADO→VALIDADO). 0740: PARIDAD VISUAL
// con el análisis BPMM reutilizando el MISMO kit `components/analisis-ui-proceso`
// (SectionCard, DAFOMatrix cuadrantes color, Badge #N, Markdown, Lectura del consultor).
// ════════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../../../../i18n';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import {
  fetchAnalisisVigentePemm,
  generarAnalisisPemm,
  validarAnalisisPemm,
  descargarMegapromptPemm,
  obtenerInformePemmHtml,
  descargarInformePemmWord,
  descargarExportadorPyPemm,
  type AnalisisPemm,
} from './api-pemm-proceso';
import {
  COLORS,
  SectionCard,
  Badge,
  Markdown,
  ListaPunteada,
  DAFOMatrix,
  type DafoData,
} from './components/analisis-ui-proceso';
import { MadurezPemmRoadmap } from './MadurezPemmRoadmap';
import { MadurezPemmTcoRoi } from './MadurezPemmTcoRoi';

const CAT_LABEL: Record<string, string> = {
  DISENO: 'Diseño',
  EJECUTANTES: 'Ejecutantes',
  PROPIETARIO: 'Propietario',
  INFRAESTRUCTURA: 'Infraestructura',
  METRICAS: 'Métricas',
};

/** DAFO derivada de lo que el análisis produce cuando la IA no la generó (espejo BPMM). */
function derivarDafoPemm(a: AnalisisPemm): DafoData {
  const cats = [...new Set(a.recomendaciones.map((r) => r.categoria))];
  return {
    fortalezas: a.fortalezas ?? [],
    amenazas: a.riesgos ?? [],
    debilidades: cats.map((c) => `Categoría a reforzar: ${CAT_LABEL[c] ?? c}`),
    oportunidades: [...a.recomendaciones]
      .sort((x, y) => x.prioridad - y.prioridad)
      .slice(0, 5)
      .map((r) => r.textoFinal),
  };
}

function btn(bg: string, color: string, border?: string): React.CSSProperties {
  return {
    fontSize: 12,
    fontWeight: 700,
    padding: '6px 12px',
    borderRadius: 6,
    border: border ?? 'none',
    background: bg,
    color,
    cursor: 'pointer',
    fontFamily: "'DM Sans',sans-serif",
  };
}

/** Botón compacto de descarga (espejo del btnMini del análisis BPMM). */
function btnMini(bg: string, color: string, disabled: boolean): React.CSSProperties {
  return {
    fontSize: 11,
    fontWeight: 700,
    padding: '3px 9px',
    borderRadius: 6,
    border: 'none',
    background: bg,
    color,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    fontFamily: "'DM Sans',sans-serif",
  };
}

export function MadurezPemmAnalisisIA({
  evaluacionId,
  observaciones,
}: {
  evaluacionId: string;
  /** Conclusión del ingeniero (Lectura del consultor; editada en la columna izquierda). */
  observaciones?: string | null;
}) {
  const { t } = useI18n();
  const k = ((t.process as Record<string, unknown>)?.madurezPemm ?? {}) as Record<string, string>;
  const tx = (key: string, fallback: string): string => k[key] ?? fallback;

  const [analisis, setAnalisis] = useState<AnalisisPemm | null>(null);
  const [loading, setLoading] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [validando, setValidando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(() => {
    setLoading(true);
    fetchAnalisisVigentePemm(evaluacionId)
      .then((r) => setAnalisis(r.data))
      .catch(() => setError(tx('iaErrorCargar', 'No se pudo cargar el análisis')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluacionId]);
  useEffect(() => {
    cargar();
  }, [cargar]);

  const generar = async () => {
    setGenerando(true);
    setError(null);
    try {
      const { data } = await generarAnalisisPemm(evaluacionId);
      setAnalisis(data);
    } catch {
      setError(
        tx('iaErrorGenerar', 'No se pudo generar el análisis (¿permiso de edición o cuota?)'),
      );
    } finally {
      setGenerando(false);
    }
  };

  const validar = async () => {
    if (!analisis) return;
    setValidando(true);
    setError(null);
    try {
      const { data } = await validarAnalisisPemm(analisis.id);
      setAnalisis(data);
    } catch {
      setError(tx('iaErrorValidar', 'No se pudo validar (¿rol directivo o alertas de Sentinel?)'));
    } finally {
      setValidando(false);
    }
  };

  const conExport = async (fn: () => Promise<unknown>) => {
    setExportando(true);
    setError(null);
    try {
      await fn();
    } catch {
      setError(tx('iaErrorExport', 'No se pudo generar el informe (¿permiso de edición?)'));
    } finally {
      setExportando(false);
    }
  };
  const descargarMega = () => conExport(() => descargarMegapromptPemm(evaluacionId));
  const abrirHtml = () =>
    conExport(async () => {
      const url = await obtenerInformePemmHtml(evaluacionId);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    });
  const descargarWord = () =>
    analisis ? conExport(() => descargarInformePemmWord(analisis.id)) : undefined;
  const descargarPy = () =>
    analisis ? conExport(() => descargarExportadorPyPemm(analisis.id)) : undefined;

  const tieneSentinelError = (analisis?.sentinelFlags ?? []).some((f) => f.severity === 'ERROR');
  const validado = analisis?.estado === 'VALIDADO';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        fontFamily: "'DM Sans',sans-serif",
      }}
    >
      {/* ── Toolbar ───────────────────────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          padding: '4px 2px',
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            color: COLORS.a1,
          }}
        >
          {tx('iaTitulo', 'Análisis V·IA Governance')}
        </span>
        <button
          style={{ ...btn(COLORS.a1, COLORS.w), opacity: generando ? 0.6 : 1 }}
          onClick={generar}
          disabled={generando}
        >
          {generando
            ? tx('iaGenerando', 'Generando…')
            : analisis
              ? tx('iaRegenerar', 'Regenerar análisis IA')
              : tx('iaGenerar', 'Generar análisis V·IA')}
        </button>
        {analisis &&
          (validado ? (
            <Badge tono="verde">{tx('iaValidado', '✓ Validado')}</Badge>
          ) : (
            <Badge tono="azul">{tx('iaGenerado', 'Generado')}</Badge>
          ))}
        <div style={{ flex: 1 }} />
        {/* Descargas: 4 botones coloreados sólo cuando VALIDADO (patrón BPMM) */}
        {validado && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button
              style={btnMini(COLORS.a1, COLORS.w, exportando)}
              onClick={descargarWord}
              disabled={exportando}
              title={tx('expWordTip', 'Informe Word corporativo (PO.34.MO.02)')}
            >
              {tx('expWord', 'Descargar informe Word')}
            </button>
            <button
              style={btnMini(COLORS.a2, COLORS.w, exportando)}
              onClick={descargarPy}
              disabled={exportando}
              title={tx(
                'expPyTip',
                'Exportador .py para reproducir el Word en claude.ai (PO.34.HE.02)',
              )}
            >
              {tx('expPy', 'Descargar exportador .py')}
            </button>
            <button
              style={btnMini(COLORS.cyan, '#0B2545', exportando)}
              onClick={descargarMega}
              disabled={exportando}
              title={tx(
                'expMegaTip',
                'Megaprompt .md para generar el informe premium en claude.ai',
              )}
            >
              {tx('expMega', 'Generar megaprompt claude.ai')}
            </button>
            <button
              style={btnMini(COLORS.verde, COLORS.w, exportando)}
              onClick={abrirHtml}
              disabled={exportando}
              title={tx('expHtmlTip', 'Informe HTML autónomo (abre en pestaña nueva)')}
            >
              {tx('expHtml', 'Generar informe HTML')}
            </button>
          </div>
        )}
      </div>

      {error && <div style={{ color: COLORS.rojo, fontSize: 13, fontWeight: 600 }}>{error}</div>}

      {loading ? (
        <SectionCard title={tx('iaTitulo', 'Análisis V·IA Governance')} accent={COLORS.cyan}>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <LoadingSpinner />
          </div>
        </SectionCard>
      ) : !analisis ? (
        <SectionCard title={tx('iaTitulo', 'Análisis V·IA Governance')} accent={COLORS.cyan}>
          <p style={{ fontSize: 13, color: COLORS.go, margin: 0, lineHeight: 1.55 }}>
            {tx(
              'iaVacio',
              'Aún no hay análisis. Genera uno: la V·IA leerá los 13 habilitadores y diagnosticará el eslabón débil, qué desbloquea el siguiente nivel y un DAFO.',
            )}
          </p>
        </SectionCard>
      ) : (
        <>
          {tieneSentinelError && (
            <div
              style={{
                background: '#FDECEC',
                border: `1px solid ${COLORS.rojo}`,
                color: '#7B0000',
                borderRadius: 8,
                padding: '8px 12px',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {tx('iaSentinel', '⚠ Sentinel marcó alertas críticas: revisa antes de validar.')}
            </div>
          )}

          {/* ── Resumen ejecutivo ── */}
          <SectionCard
            title={tx('iaResumen', 'Resumen ejecutivo')}
            accent={COLORS.cyan}
            right={
              validado ? (
                <Badge tono="verde">{tx('iaValidado', 'Validado')}</Badge>
              ) : (
                <Badge tono="azul">{tx('iaBorrador', 'Borrador IA')}</Badge>
              )
            }
          >
            {observaciones?.trim() && (
              <p
                style={{
                  fontSize: 13,
                  color: COLORS.ink,
                  lineHeight: 1.55,
                  margin: '0 0 8px',
                  borderLeft: `3px solid ${COLORS.cyan}`,
                  paddingLeft: 10,
                }}
              >
                <strong style={{ color: COLORS.a1 }}>
                  {tx('iaLecturaConsultor', 'Lectura del consultor')}:{' '}
                </strong>
                {observaciones.trim()}
              </p>
            )}
            <p style={{ fontSize: 13, color: COLORS.ink, lineHeight: 1.55, margin: 0 }}>
              {analisis.conclusionEjecutiva || analisis.diagnosticoNarrativo.slice(0, 280)}
            </p>
            {analisis.recomendaciones.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                {[...analisis.recomendaciones]
                  .sort((a, b) => a.prioridad - b.prioridad)
                  .slice(0, 3)
                  .map((r, i) => (
                    <span
                      key={i}
                      style={{
                        fontSize: 11,
                        padding: '3px 9px',
                        borderRadius: 12,
                        background: COLORS.gc,
                        color: COLORS.a1,
                        fontWeight: 600,
                      }}
                    >
                      #{r.prioridad} {CAT_LABEL[r.categoria] ?? r.categoria}
                    </span>
                  ))}
              </div>
            )}
          </SectionCard>

          {/* ── Matriz DAFO ── */}
          <SectionCard title={tx('iaDafo', 'Matriz DAFO')} accent={COLORS.a2}>
            <DAFOMatrix data={analisis.matrizDafo ?? derivarDafoPemm(analisis)} />
          </SectionCard>

          {/* ── Diagnóstico ── */}
          <SectionCard title={tx('iaDiagnostico', 'Diagnóstico')} accent={COLORS.a1}>
            <Markdown text={analisis.diagnosticoNarrativo} />
          </SectionCard>

          {/* ── Recomendaciones ── */}
          {analisis.recomendaciones.length > 0 && (
            <SectionCard
              title={tx('iaRecomendaciones', 'Recomendaciones')}
              accent={COLORS.verde}
              hint={String(analisis.recomendaciones.length)}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[...analisis.recomendaciones]
                  .sort((a, b) => a.prioridad - b.prioridad)
                  .map((r, i) => (
                    <div
                      key={i}
                      style={{
                        border: `1px solid ${COLORS.gm}`,
                        borderLeft: `3px solid ${COLORS.a2}`,
                        borderRadius: 8,
                        padding: '10px 12px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginBottom: 4,
                          flexWrap: 'wrap',
                        }}
                      >
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 800,
                            color: COLORS.w,
                            background: COLORS.a1,
                            borderRadius: 10,
                            padding: '1px 8px',
                          }}
                        >
                          #{r.prioridad}
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 800, color: COLORS.a2 }}>
                          {CAT_LABEL[r.categoria] ?? r.categoria}
                          {r.habilitador ? ` · ${r.habilitador}` : ''}
                        </span>
                        {r.nivelGatillo && (
                          <Badge tono="azul">→ {r.nivelGatillo.replace('P', 'P-')}</Badge>
                        )}
                      </div>
                      <div style={{ fontSize: 13, color: COLORS.ink, lineHeight: 1.5 }}>
                        {r.textoFinal}
                      </div>
                      {r.justificacion && (
                        <div
                          style={{
                            fontSize: 11,
                            color: COLORS.go,
                            marginTop: 4,
                            fontStyle: 'italic',
                            lineHeight: 1.45,
                          }}
                        >
                          {r.justificacion}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </SectionCard>
          )}

          {/* ── Hoja de Ruta (PICK + Kotter + DAFO cruces · 0741) ── */}
          {analisis.roadmap && <MadurezPemmRoadmap roadmap={analisis.roadmap} />}

          {/* ── Estudio de inversión TCO/ROI (máster Actividad 3 · 0744) ── */}
          <MadurezPemmTcoRoi
            analisisId={analisis.id}
            initial={analisis.tcoRoi}
            readOnly={validado}
          />

          {/* ── Fortalezas / Riesgos ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <SectionCard title={tx('iaFortalezas', 'Fortalezas')} accent={COLORS.verde}>
              <ListaPunteada color={COLORS.verde} items={analisis.fortalezas} />
            </SectionCard>
            <SectionCard title={tx('iaRiesgos', 'Riesgos')} accent={COLORS.rojo}>
              <ListaPunteada color={COLORS.rojo} items={analisis.riesgos} />
            </SectionCard>
          </div>

          {/* ── Validar ── */}
          {!validado && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                style={{
                  ...btn(COLORS.verde, COLORS.w),
                  opacity: validando || tieneSentinelError ? 0.5 : 1,
                }}
                onClick={validar}
                disabled={validando || tieneSentinelError}
              >
                {validando ? tx('iaValidando', 'Validando…') : tx('iaValidar', 'Validar análisis')}
              </button>
              <span style={{ fontSize: 10, color: COLORS.go, marginLeft: 'auto' }}>
                {analisis.proveedor ?? ''} {analisis.modeloUsado ?? ''}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
